// @flow
import * as React from 'react'
import type { Municipality } from 'sources/types'
import * as topojson from 'topojson-client'
import config from 'config'
import type { GeojsonFeature } from 'components/map/component'
import type { Extents } from 'components/scale/reducer'

export type D3Map<T> = {
  clear: () => void,
  empty: () => boolean,
  empty: () => boolean,
  entries: () => Array<[string, T]>,
  get: string => T,
  has: string => boolean,
  keys: () => Array<string>,
  remove: string => boolean,
  set: (string, T) => D3Map<T>,
  size: () => number,
  values: () => Array<T>
}

type MapProps = {
  currentExtents: Extents,
  gemeinden: D3Map<Municipality>,
  geometryCollection: { type: string, geometries: Array<GeojsonFeature> },
  mapId: string,
  pathGenerator: GeojsonFeature => string
}

/**
 * Draw a choropleth map
 * @param {object} props
 * @param props.gemeinden
 * @constructor
 */
export function ChoroplethMap ({
  currentExtents,
  gemeinden,
  geometryCollection,
  mapId = '',
  pathGenerator
}: MapProps): React.Element<'g'> | null {
  if (!gemeinden || gemeinden.size() <= 0 || !currentExtents) {
    return null
  }

  // Get an indexed version of the municipalities, so we can find the values
  // for each
  const indexedMunicipalities = gemeinden

  const gemeindeFeatures: Array<GeojsonFeature> = topojson.feature(
    config.schweiz,
    geometryCollection
  ).features

  function drawSwissMunicipalities (
    colorGenerator: number => string,
    areas: Array<GeojsonFeature>,
    areaIdProperty = 'GMDNR',
    idGenerator = property => property
  ): Array<React.Element<'path' | 'g'>> {
    return areas.map((feature: GeojsonFeature, index: number) => {
      const municipalityId = idGenerator(+feature.properties[areaIdProperty])

      const municipalityData = indexedMunicipalities.get(
        municipalityId.toString()
      )

      if (!municipalityData) {
        console.log(`[ChoroplethMap]: Data for ID ${municipalityId} not found.`)
        return null
      }

      const range = currentExtents[1] - currentExtents[0]

      let generatedColor: string = colorGenerator(
        (+municipalityData.Belastung - currentExtents[0]) / range
      )

      let colouredMunicipalityPath = (
        <path
          key={municipalityId}
          d={pathGenerator(feature)}
          strokeWidth={0}
          vectorEffect='non-scaling-stroke'
          stroke={generatedColor}
          fill={generatedColor}
          id={`${mapId}-gemeinde-${municipalityId}`}
          className='gemeinde'
        />
      )

      return colouredMunicipalityPath
    })
  }

  const municipalityPaths = drawSwissMunicipalities(
    config.colorGenerator,
    gemeindeFeatures
  )

  return (
    <g className='all municipalities non-interactive'>{municipalityPaths}</g>
  )
}



// WEBPACK FOOTER //
// ./src/components/choroplethMap/choroplethMapComponent.js